import React, { useState } from 'react';
import { fetchWithAuth } from '../../utils/api';

interface QuotaRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  gpu?: {
    provider: string;
    provider_instance_id: string;
    display_name: string;
    region: string;
  };
  // Prefill alternative when opened from a launch-error flow (no full SemanticGPU in hand)
  prefillProvider?: string;
  prefillInstanceType?: string;
  prefillRegion?: string;
  prefillDisplayName?: string;
}

export default function QuotaRequestModal({
  isOpen,
  onClose,
  gpu,
  prefillProvider,
  prefillInstanceType,
  prefillRegion,
  prefillDisplayName,
}: QuotaRequestModalProps) {
  const [useCase, setUseCase] = useState('');
  const [expectedUsage, setExpectedUsage] = useState('');
  const [businessJustification, setBusinessJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const effectiveProvider = gpu?.provider ?? prefillProvider ?? '';
  const effectiveInstanceType = gpu?.provider_instance_id ?? prefillInstanceType ?? '';
  const effectiveRegion = gpu?.region ?? prefillRegion ?? '';
  const effectiveDisplayName = gpu?.display_name ?? prefillDisplayName ?? '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetchWithAuth('/api/gpu/request-quota', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: effectiveProvider,
          instance_type: effectiveInstanceType,
          region: effectiveRegion,
          use_case: useCase,
          expected_usage: expectedUsage,
          business_justification: businessJustification
        })
      });

      if (response.ok) {
        // Success - show confirmation and close
        alert('Quota request submitted successfully! Our team will review and contact you within 24 hours.');
        onClose();
        // Reset form
        setUseCase('');
        setExpectedUsage('');
        setBusinessJustification('');
      } else {
        const error = await response.json();
        alert(`Error submitting request: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">Request GPU Access</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {effectiveDisplayName || effectiveInstanceType ? (
          <div className="mb-4 p-3 bg-gray-700 rounded-lg">
            <div className="text-sm text-gray-300">Requesting access to:</div>
            {effectiveDisplayName && (
              <div className="text-white font-medium">{effectiveDisplayName}</div>
            )}
            <div className="text-sm text-gray-400">
              {effectiveInstanceType || 'instance type'}
              {effectiveRegion && ` in ${effectiveRegion}`}
              {effectiveProvider && ` (${effectiveProvider.toUpperCase()})`}
            </div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-gray-700 rounded-lg">
            <div className="text-sm text-gray-300">Describe the GPU type you need below.</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Primary Use Case *
            </label>
            <select
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a use case</option>
              <option value="quantitative_analysis">Quantitative Analysis</option>
              <option value="monte_carlo_simulation">Monte Carlo Simulation</option>
              <option value="machine_learning_trading">ML Trading Models</option>
              <option value="time_series_analysis">Time Series Analysis</option>
              <option value="risk_modeling">Risk Modeling</option>
              <option value="backtesting">Strategy Backtesting</option>
              <option value="research">Research & Development</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Expected Usage *
            </label>
            <select
              value={expectedUsage}
              onChange={(e) => setExpectedUsage(e.target.value)}
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select expected usage</option>
              <option value="occasional">Occasional (&lt; 10 hours/week)</option>
              <option value="regular">Regular (10-40 hours/week)</option>
              <option value="heavy">Heavy (&gt; 40 hours/week)</option>
              <option value="burst">Burst processing (short intensive periods)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Business Justification
            </label>
            <textarea
              value={businessJustification}
              onChange={(e) => setBusinessJustification(e.target.value)}
              rows={3}
              data-gramm="false"
              data-gramm_editor="false"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Brief description of why you need this GPU type..."
            />
          </div>

          <div className="text-xs text-gray-400">
            * Required fields. Quota requests are typically processed within 24 hours.
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}